import { useState } from "react";

import { RichText } from "@atlassiansox/bxp-content-framework";

import {
    Carousel,
    Column,
    Row,
    Stack,
    useAnalytics,
    useAnalyticsAttributes
} from "../../components";
import { UiListCarouselModel } from "../../models";
import { renderViewModel } from "../../render-view-model";

import { LinkItem, StackPadding } from "./UiListCarousel.styles";

export type UiListCarouselProps = {
    readonly model: UiListCarouselModel;
};

const UiListCarousel = ({
    model: {
        content: {
            title,
            captions,
            autoplay,
            accentColor,
            hideDotsOnDesktop,
            orientation,
            dotLabel,
            prevArrowLabel,
            nextArrowLabel
        }
    }
}: UiListCarouselProps) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const { sendTrackEvent } = useAnalytics();
    const analyticsAttributes = useAnalyticsAttributes();

    return (
        <Row data-testid="list-carousel">
            <Column
                xs={{ span: "0", top: "none", bottom: "none" }}
                lg={{
                    span: "4",
                    order: orientation === "Image left" ? "2" : "1"
                }}
            >
                <StackPadding>
                    <Stack spacing="compact">
                        {captions
                            .filter(({ type }) => type === "caption")
                            .map((caption, index) => (
                                <LinkItem
                                    key={caption.id}
                                    selected={index === selectedIndex}
                                    accentColor={accentColor}
                                    onClick={() => {
                                        setSelectedIndex(index);
                                        sendTrackEvent(
                                            "carouselCaptionClicked",
                                            {
                                                ...analyticsAttributes,
                                                eventContainer:
                                                    "uiListCarousel",
                                                event: "clicked",
                                                eventComponent: "caption",
                                                label: caption.content.title,
                                                labelEnglish:
                                                    caption.content.entryTitle,
                                                parentLabel: title,
                                                uuid: caption.id
                                            }
                                        );
                                    }}
                                >
                                    <RichText
                                        document={caption.content.caption}
                                    />
                                </LinkItem>
                            ))}
                    </Stack>
                </StackPadding>
            </Column>
            <Column
                xs={{ top: "none" }}
                lg={{
                    span: "8",
                    top: hideDotsOnDesktop ? "compact" : undefined,
                    order: orientation === "Image left" ? "1" : "2"
                }}
            >
                <Carousel
                    onChange={(index: number) => setSelectedIndex(index)}
                    selectedIndex={selectedIndex}
                    dotsOnDesktop={!hideDotsOnDesktop}
                    dotsOnMobile={false}
                    initialAutoplay={autoplay}
                    dotLabel={dotLabel}
                    prevArrowLabel={prevArrowLabel}
                    nextArrowLabel={nextArrowLabel}
                    controlsAlignStart={orientation === "Image left"}
                >
                    {captions
                        .filter(({ type }) => type === "caption")
                        .map((caption) =>
                            renderViewModel(caption.content.content)
                        )}
                </Carousel>
            </Column>
            <Column xs={{ top: "none", bottom: "none" }} lg={{ span: "0" }}>
                <Carousel
                    onChange={(index: number) => setSelectedIndex(index)}
                    selectedIndex={selectedIndex}
                    dotsOnDesktop={false}
                    dotsOnMobile={true}
                    dotLabel={dotLabel}
                    prevArrowLabel={prevArrowLabel}
                    nextArrowLabel={nextArrowLabel}
                >
                    {captions
                        .filter(({ type }) => type === "caption")
                        .map((caption) => (
                            <LinkItem
                                key={caption.id}
                                selected={true}
                                accentColor={accentColor}
                            >
                                <RichText document={caption.content.caption} />
                            </LinkItem>
                        ))}
                </Carousel>
            </Column>
        </Row>
    );
};

export default UiListCarousel;
